import React from "react";
import * as UI from "./ui.jsx";
import { authorizedFetch } from "./boot.js";
import { sydneyWhen } from "./time.js";
import { count as n, duration, errorCode, visiblePoll } from "./stats_shared.js";

// The Stats X-ray views tab, a copy of the Repository's portal (owner, 24
// September 2026). Counts are every open, exactly as the Repository's
// view-counts feed sent them; vets and X-ray time are the summary's; each lot's
// individual views are our copy of the Repository's views feed, read through
// vendor.stats.lot_views@1. The page counts nothing itself: the only derived
// figures are one lot's stored views grouped by vet, which is how the portal
// presents the same rows.

// Our copy is refreshed once a minute; reading it faster would only repeat it.
const DETAIL_POLL_MS = 60_000;

const ACTIONS = { dicom: "View X-ray", mp4: "View scope video", pdf: "View report" };

export const actionLabel = (mediaType) => ACTIONS[mediaType] || "—";

// The names as the Repository sent them. "Not recorded" is the portal's own
// wording for a view with no nomination; a withheld one is not disclosed yet,
// which is a different fact and reads differently.
export function nominatedPerson(view) {
  if (Array.isArray(view.clients) && view.clients.length) return view.clients.join(", ");
  if (view.clientsReason === "pre-disclosure") return "Not disclosed yet";
  if (view.clientsReason === "none") return "Not recorded";
  return "—";
}

const breeding = (horse) => [horse?.sire, horse?.dam].filter(Boolean).join(" × ");
const vets = (value) => (typeof value === "number" ? `${value} vet${value === 1 ? "" : "s"}` : null);
const plural = (value, one, many) => `${value} ${value === 1 ? one : many}`;
const lotOrder = (a, b) =>
  String(a.lotNumber || a.lotId).localeCompare(String(b.lotNumber || b.lotId), undefined, { numeric: true });

export function XrayLots({ snap, onOpen }) {
  const { Card, Icon } = UI;
  const [query, setQuery] = React.useState("");
  const counts = snap.viewCounts;
  const horses = snap.horses || {};
  const rows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return (snap.lots || [])
      .filter((lot) => !q
        || String(lot.lotNumber || lot.lotId).toLowerCase().includes(q)
        || breeding(horses[lot.lotId]).toLowerCase().includes(q))
      .slice()
      .sort(lotOrder);
  }, [snap, query]);

  return (
    <Card className="flush xray-lots" style={{ marginTop: 18 }}>
      <div className="xray-lots-head">
        <p className="caption">
          {counts
            ? `Counts as of ${sydneyWhen(counts.observedAt)}`
            : "Every-open view counts have not been read yet."}
          {" "}Each time a vet opens a lot's X-rays, scope video or report is one view, as the
          Repository counts them.
        </p>
        <div className="searchbox">
          <Icon.search style={{ width: 15, height: 15, color: "var(--fg-muted)" }}/>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Lot or horse…"
                 aria-label="Find a lot"/>
        </div>
      </div>
      <table className="table stack-table">
        <thead><tr>
          <th>Lot</th>
          <th>Horse</th>
          <th style={{ textAlign: "right" }}>X-ray views</th>
          <th style={{ textAlign: "right" }}>Scope views</th>
          <th style={{ textAlign: "right" }}>Report views</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan="5" className="stack-full" style={{ textAlign: "center", padding: 36, color: "var(--fg-muted)" }}>
              {query ? "No lot matches that search." : "The catalogue returned no lots."}
            </td></tr>
          )}
          {rows.map((lot) => {
            const number = String(lot.lotNumber || lot.lotId);
            const counted = counts?.lots?.[lot.lotId];
            const quiet = counted && !counted.xray && !counted.scope && !counted.report;
            const withVets = lot.uniqueVets > 0 ? ` (${vets(lot.uniqueVets)})` : "";
            return (
              <tr key={lot.lotId} className={quiet ? "quiet" : undefined}>
                <td data-label="Lot">
                  <button type="button" className="lot-detail-link" onClick={() => onOpen(String(lot.lotId))}
                          aria-label={`Open X-ray views for lot ${number}`}>{number}</button>
                </td>
                <td data-label="Horse">{breeding(horses[lot.lotId]) || "—"}</td>
                <td data-label="X-ray views" style={{ textAlign: "right" }}>{n(counted?.xray)}{withVets}</td>
                <td data-label="Scope views" style={{ textAlign: "right" }}>{n(counted?.scope)}</td>
                <td data-label="Report views" style={{ textAlign: "right" }}>{n(counted?.report)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

export function XrayLotPage({ saleId, lot, counted, horse, onBack }) {
  const { Card } = UI;
  const panelRef = React.useRef(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [retry, setRetry] = React.useState(0);
  const lotId = String(lot.lotId);
  const number = String(lot.lotNumber || lot.lotId);
  const name = breeding(horse);

  React.useEffect(() => { panelRef.current?.focus(); }, [lotId]);

  React.useEffect(() => {
    let cancelled = false;
    let requestNumber = 0;
    const controller = new AbortController();
    const load = async () => {
      const request = ++requestNumber;
      const params = new URLSearchParams({ sale: saleId, lot: lotId });
      try {
        const res = await authorizedFetch(`/collections/vendor.stats.lot_views@1/?${params.toString()}`,
          { signal: controller.signal });
        const body = await res.json().catch(() => ({}));
        if (cancelled || request !== requestNumber) return;
        if (!res.ok) { setError({ code: errorCode(body, res.status), status: res.status }); return; }
        if (body.sale !== saleId || body.lot !== lotId || !Array.isArray(body.views)) {
          setError({ code: "STALE_RESPONSE" });
          return;
        }
        setError(null);
        setResult(body);
      } catch (caught) {
        if (cancelled || request !== requestNumber || caught?.name === "AbortError") return;
        setError({ code: "BROWSER_FETCH_FAILED" });
      }
    };
    load();
    // No If-None-Match: this is a governed Collection through the Soft System
    // adapter, which has no conditional responses. It takes the cadence only.
    const stop = visiblePoll(load, DETAIL_POLL_MS);
    return () => { cancelled = true; controller.abort(); stop(); };
  }, [saleId, lotId, retry]);

  return (
    <div className="xray-lot" ref={panelRef} tabIndex="-1" aria-label={`X-ray views for lot ${number}`}>
      <Card title={`Lot ${number}`} subtitle={name || undefined} style={{ marginTop: 18 }}
            right={<button type="button" className="btn xray-back" onClick={onBack}>Back to all lots</button>}>
        <div className="qstrip" style={{ margin: "0 0 18px", display: "flex" }}>
          <Tile label="X-ray views" value={n(counted?.xray)} sub={vets(lot.uniqueVets)}/>
          <Tile label="X-ray viewing time" value={duration(lot.xrayDurationMs)}
                sub="Accepted viewing time may still increase"/>
          <Tile label="Scope views" value={n(counted?.scope)}/>
          <Tile label="Report views" value={n(counted?.report)}/>
        </div>
        {!result && !error && <p role="status">Loading viewing detail…</p>}
        {!result && error && (
          <div role="alert">
            <p>
              {error.status === 401 || error.status === 403
                ? "This lot's viewing detail is not available for this session."
                : error.status === 404
                  ? "That lot's viewing detail is not available."
                  : "Viewing detail could not be loaded."}
            </p>
            <button type="button" className="btn" onClick={() => setRetry((value) => value + 1)}>Retry</button>
          </div>
        )}
        {result && <LotViews result={result} counted={counted} refreshFailed={!!error}/>}
      </Card>
    </div>
  );
}

function LotViews({ result, counted, refreshFailed }) {
  const [open, setOpen] = React.useState(() => new Set());
  const views = result.views;
  const dicom = views.filter((view) => view.mediaType === "dicom");

  if (!result.syncedAt) {
    return (
      <p role="status">
        Viewing detail is not available yet: our copy has not read the Repository yet. The counts
        above are the Repository's own.
      </p>
    );
  }

  // The live count can run ahead of our copy by up to a sync. Say by how much
  // rather than letting the tiles and the tables disagree silently. A bounded
  // read holds only the newest views, so the gap would be the bound's, not the
  // copy's; it is stated instead.
  const behind = !result.truncated && typeof counted?.xray === "number" ? counted.xray - dicom.length : 0;
  const quiet = counted && !counted.xray && !counted.scope && !counted.report && views.length === 0;

  const byVet = new Map();
  for (const view of dicom) {
    const vet = byVet.get(view.vetId)
      || { vetId: view.vetId, vetName: view.vetName, vetClinic: view.vetClinic, views: 0, first: null, last: null };
    vet.views += 1;
    if (!vet.first || view.openedAt < vet.first) vet.first = view.openedAt;
    if (!vet.last || view.openedAt > vet.last) vet.last = view.openedAt;
    byVet.set(view.vetId, vet);
  }
  const vetRows = [...byVet.values()].sort((a, b) => (a.last < b.last ? 1 : a.last > b.last ? -1 : 0));
  const toggle = (vetId) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(vetId)) next.delete(vetId); else next.add(vetId);
    return next;
  });

  return (
    <>
      <p className="caption">
        Detail updated {sydneyWhen(result.syncedAt)}
        {behind > 0 && <> · {plural(behind, "view is", "views are")} not in the detail yet.</>}
        {result.truncated && <> · Showing the most recent {n(result.bound)} views of this lot.</>}
      </p>
      {refreshFailed && (
        <p role="status" className="caption">The latest refresh failed. Showing the detail read earlier.</p>
      )}
      {quiet && <p role="status">No one has opened this lot's X-rays, scope or report yet.</p>}
      {!quiet && views.length === 0 && <p role="status">No views are in the detail yet.</p>}

      {vetRows.length > 0 && (
        <section className="xray-by-vet">
          <h3>X-ray activity by vet</h3>
          <table className="table stack-table">
            <thead><tr>
              <th>Vet</th><th>Practice</th><th style={{ textAlign: "right" }}>X-ray views</th>
              <th>First seen</th><th>Last seen</th>
            </tr></thead>
            <tbody>
              {vetRows.map((vet) => {
                const expanded = open.has(vet.vetId);
                const label = vet.vetName || vet.vetId;
                return (
                  <React.Fragment key={vet.vetId}>
                    <tr className="vet-row">
                      <td data-label="Vet">
                        <button type="button" className="vet-toggle" aria-expanded={expanded ? "true" : "false"}
                                onClick={() => toggle(vet.vetId)}>{label}</button>
                      </td>
                      <td data-label="Practice">{vet.vetClinic || "—"}</td>
                      <td data-label="X-ray views" style={{ textAlign: "right" }}>{n(vet.views)}</td>
                      <td data-label="First seen" className="mono">{sydneyWhen(vet.first)}</td>
                      <td data-label="Last seen" className="mono">{sydneyWhen(vet.last)}</td>
                    </tr>
                    {expanded && (
                      <tr className="vet-actions-row">
                        <td colSpan="5" className="stack-full">
                          <div className="vet-actions">
                            <h4>Viewing actions by {label}</h4>
                            <table className="table stack-table">
                              <thead><tr><th>When</th><th>Action</th><th>Nominated person</th></tr></thead>
                              <tbody>
                                {views.filter((view) => view.vetId === vet.vetId).map((view) => (
                                  <tr key={view.viewId}>
                                    <td data-label="When" className="mono">{sydneyWhen(view.openedAt)}</td>
                                    <td data-label="Action">{actionLabel(view.mediaType)}</td>
                                    <td data-label="Nominated person">{nominatedPerson(view)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {views.length > 0 && (
        <section className="xray-activity">
          <h3>Activity</h3>
          <table className="table stack-table">
            <thead><tr>
              <th>When</th><th>Vet</th><th>Activity</th><th>Nominated person</th>
              <th style={{ textAlign: "right" }}>Duration</th>
            </tr></thead>
            <tbody>
              {views.map((view) => (
                <tr key={view.viewId}>
                  <td data-label="When" className="mono">{sydneyWhen(view.openedAt)}</td>
                  <td data-label="Vet">{view.vetName || view.vetId}</td>
                  <td data-label="Activity">{actionLabel(view.mediaType)}</td>
                  <td data-label="Nominated person">{nominatedPerson(view)}</td>
                  <td data-label="Duration" className="mono" style={{ textAlign: "right" }}>{duration(view.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div className="qstat">
      <div className="qstat-label">{label}</div>
      <div className="num stat-tile-value">{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
